import { useState, Dispatch, SetStateAction } from "react";
import { CommentDto } from "../../api/Api";

type FormWithComments = {
  comments: CommentDto[];
};

// The entity's update mutation, injected by the caller (useUpdateCounterparty /
// useUpdateBroker). Adding/deleting a lifecycle comment persists the whole entity
// without closing the dialog, so callers pass a mutation instance with no onSuccess.
type EntityUpdate<TEntity> = {
  mutateAsync: (entity: TEntity) => Promise<unknown>;
  isPending: boolean;
};

type UseLifecycleCommentsArgs<TForm extends FormWithComments, TEntity> = {
  formData: TForm;
  setFormData: Dispatch<SetStateAction<TForm>>;
  buildEntityWithComments: (updatedComments: CommentDto[]) => TEntity;
  update: EntityUpdate<TEntity>;
};

type UseLifecycleCommentsResult = {
  currentComment: string;
  setCurrentComment: Dispatch<SetStateAction<string>>;
  addComment: (comment: CommentDto) => Promise<void>;
  deleteComment: (timestamp: string) => Promise<void>;
  isSaving: boolean;
};

export const useLifecycleComments = <
  TForm extends FormWithComments,
  TEntity
>(
  args: UseLifecycleCommentsArgs<TForm, TEntity>
): UseLifecycleCommentsResult => {
  const { formData, setFormData, buildEntityWithComments, update } = args;
  const [currentComment, setCurrentComment] = useState("");

  const addComment = async (comment: CommentDto) => {
    const updatedComments = [...formData.comments, comment];
    await update.mutateAsync(buildEntityWithComments(updatedComments));
    setCurrentComment("");
    setFormData((prev) => ({ ...prev, comments: updatedComments }));
  };

  const deleteComment = async (timestamp: string) => {
    const updatedComments = formData.comments.filter(
      (c) => c.timestamp !== timestamp
    );
    await update.mutateAsync(buildEntityWithComments(updatedComments));
    setFormData((prev) => ({ ...prev, comments: updatedComments }));
  };

  return {
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment,
    isSaving: update.isPending
  };
};
